import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useState } from "react";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import { ErrorPageContent } from "@ploy-ui/core";
import { ErrorHandler } from "@ploy-lib/core";
import { Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DployTextField, DploySelectionField } from "@ploy-ui/form-fields";
import { useCalculationField, useDispatch } from "@ploy-lib/calculation";
import { defineMessages, useIntl } from "react-intl";
import clsx from "clsx";
import { useFormikContext } from "formik";
import { LabelComponent } from "../../field";
import { useAppLoad } from "../../appLoadContext";
import { CustomerRole } from "@ploy-lib/types";
addRegisteredSectionLayout({
    name: "ChildListSection",
    displayName: "ChildListSection",
    settings: {
        editableOptions: {
            minimized: true
        }
    }
});
const useStyles = makeStyles((theme) => ({
    root: {},
    list: {
        listStyle: "none",
        margin: 0,
        padding: 0,
        width: "100%"
    },
    listItem: {
        backgroundColor: theme.palette.primary.light,
        border: `1px solid ${theme.palette.secondary.light}`,
        padding: theme.spacing(2),
        paddingTop: 16,
        paddingBottom: theme.spacing(4)
    },
    childNumber: {
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(2)
    },
    header: {
        fontWeight: 700,
        paddingBottom: theme.spacing(4),
        padding: theme.spacing(0.5)
    },
    errorContainer: {
        marginTop: "-14px"
    }
}), { name: "DployChildListSection" });
const intlMessages = defineMessages({
    childrenLivingSituation: {
        id: "template-form.sections.child-list.child-living-sitiation",
        description: "Is the child living at home or not?",
        defaultMessage: "Is the child living with you?"
    },
    childFullResponsibility: {
        id: "template-form.sections.child-list.child-living-sitiation.full",
        description: "Full responsibility for the child",
        defaultMessage: "Full"
    },
    childPartialResponsibility: {
        id: "template-form.sections.child-list.child-living-sitiation.partial",
        description: "Partial responsibility for the child",
        defaultMessage: "Partial"
    },
    childHelperText: {
        id: "template-form.sections.child-list.child-helpertext",
        description: "Helpertext for each child",
        defaultMessage: "We need to know if you have full or partial responsibility for the child"
    },
    sectionHelperText: {
        id: "template-form.sections.child-list.section-helpertext",
        description: "Helpertext for the section",
        defaultMessage: "We have retrieved the children you are responsible for"
    },
    genderAndAge: {
        id: "template-form.sections.child-list.genderAndAge",
        description: "Child's gender and age",
        defaultMessage: "{gender, select, female {Girl} male {Boy} other {}} ({age})"
    },
    childSummary: {
        id: "template-form.sections.child-list.childSummary",
        description: "Accessible label describing one child",
        defaultMessage: "{genderAndAge}, born {birthDate}"
    },
    title: {
        id: "template-form.sections.child-list.title",
        description: "Title for child list section",
        defaultMessage: "Children"
    },
    subTitle: {
        id: "template-form.sections.child-list.sub-title",
        description: "Your and divided children",
        defaultMessage: "Your own and common children"
    }
});
const getGender = (ssn) => {
    const genderIdentifierDigit = ssn[8];
    return Number(genderIdentifierDigit) % 2 === 0 ? "female" : "male";
};
const getAge = (birthday) => {
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthsDiff = today.getMonth() - birthDate.getMonth();
    if (monthsDiff < 0 || // Birth-month is a later month than today
        (monthsDiff === 0 && today.getDate() < birthDate.getDate()) // Birth-month is this month, but birth-date is not "here" yet
    )
        age -= 1;
    return age;
};
const calcRulesNamespace = "Calculator";
const calcRulesName = "CHILDREN";
const ChildListSectionInternal = memo((props) => {
    const { className, minimized, allFields = [] } = props;
    const { touched, errors } = useFormikContext();
    const error = errors[calcRulesNamespace]?.[calcRulesName];
    const isFieldTouched = !!touched[calcRulesNamespace]?.[calcRulesName];
    const intl = useIntl();
    const classes = useStyles(props);
    const dispatch = useDispatch();
    const childrenField = useCalculationField(calcRulesNamespace, calcRulesName);
    // ChilsListSection supports having a "CustomerId"-field to filter ApplicationChild[] based on the given CustomerId-field
    const customerId = useCalculationField({
        name: allFields[0]?.name,
        namespace: allFields[0]?.namespace
    })?.value;
    const allChildren = childrenField?.value || [];
    const children = allChildren
        .filter(x => getAge(x.birthDate) < 18)
        .filter(x => 
    /**********************************************************************************
     * In InternalWeb's new application-form, we fetch all children to this component, so we must filter the kids based on a provided customerId.
     * - Use field: "Calculator.CustomerId" to only display children for main customer
     * - Use field: "Calculator.HouseholdMemberCustomerId" to only display children for the ApplicationCustomer marked with role "Spouse"/ "Cohabitant"
     * In Snippet, we only fetch the children for the given customer-context in the form (Customer/ Spouse), therefore no need to use a CustomerId field for filtering children
     * - "Calculator.CHILDREN" is used to provide form-validations for the required "customerResponsibility"-inputs
     **********************************************************************************/
    allFields[0]?.name === "CHILDREN" || x.customerId === customerId);
    const [showHelperText, setShowHelperText] = useState(false);
    const toggleHelperText = useCallback((e) => {
        setShowHelperText(show => !show);
        e.stopPropagation();
    }, []);
    const { loggedInCustomerRole } = useAppLoad();
    const values = {
        isMainCustomer: loggedInCustomerRole === CustomerRole.Customer
    };
    if (children.length === 0)
        return null;
    const subtitle = intl.formatMessage(intlMessages.subTitle, values);
    const sectionHelperText = intl.formatMessage(intlMessages.sectionHelperText, values);
    return (_jsxs(Grid, { container: true, className: clsx(className, classes.root), children: [_jsxs(Grid, { container: true, item: true, className: classes.header, children: [_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "h3", children: intl.formatMessage(intlMessages.title) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(LabelComponent, { labelText: _jsx(Typography, { variant: "body2", children: allFields[0]?.label ?? subtitle }), trimmedHelperText: allFields[0]?.helperText ?? sectionHelperText, displayHelperTextAboveField: true, field: allFields[0], showHelperText: showHelperText, toggleHelperText: toggleHelperText }) })] }), _jsx(Grid, { container: true, item: true, component: "ul", className: classes.list, children: children.map(child => (_jsx(ChildListItem, { classes: classes, minimized: minimized, intl: intl, isFieldTouched: isFieldTouched, dispatch: dispatch, error: error, allChildren: allChildren, disabled: childrenField?.enabled === false, child: child }, String(child.ssn)))) })] }));
});
const hasValidationError = (fieldIsTouched, child) => fieldIsTouched && typeof child.customerResponsibility === "undefined";
const ChildListSection = (props) => (_jsx(ErrorHandler, { fallback: e => (_jsx(ErrorPageContent, { className: props.className, onClick: props.onClick, header: _jsxs("span", { children: [ChildListSection.displayName, " failed to render."] }) })), children: _jsx(ChildListSectionInternal, { ...props }) }));
const ChildListItem = (props) => {
    const { classes, minimized, intl, isFieldTouched, dispatch, error, allChildren, disabled, child } = props;
    const genderAndAge = intl.formatMessage(intlMessages.genderAndAge, {
        age: getAge(child.birthDate),
        gender: getGender(child.ssn)
    });
    const summary = intl.formatMessage(intlMessages.childSummary, {
        genderAndAge,
        birthDate: intl.formatDate(child.birthDate, { dateStyle: "short" })
    });
    const [showHelperText, setShowHelperText] = useState(false);
    const toggleHelperText = useCallback((e) => {
        setShowHelperText(show => !show);
        e.stopPropagation();
    }, []);
    const helperTextId = `child-responsibility-${child.applicationChildId}-helper`;
    const labelId = `child-responsibility-${child.applicationChildId}-label`;
    return (_jsx(Grid, { container: true, item: true, component: "li", className: classes.listItem, xs: minimized ? 2 : 12, spacing: minimized ? undefined : 1, children: _jsxs(Grid, { item: true, container: true, xs: 12, role: "group", "aria-label": summary, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(DployTextField, { id: child.ssn, fullWidth: minimized, className: classes.childNumber, label: genderAndAge, value: intl.formatDate(child.birthDate, {
                            dateStyle: "short" //dd.MM.yyyy
                        }), disabled: true }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(DploySelectionField, { "aria-labelledby": labelId, "aria-describedby": showHelperText ? helperTextId : undefined, label: _jsx(LabelComponent, { labelText: intl.formatMessage(intlMessages.childrenLivingSituation), labelId: labelId, trimmedHelperText: intl.formatMessage(intlMessages.childHelperText), variant: "outlined", showHelperText: showHelperText, toggleHelperText: toggleHelperText, helperTextId: helperTextId }), items: [
                            {
                                key: 2,
                                value: intl.formatMessage(intlMessages.childFullResponsibility)
                            },
                            {
                                key: 1,
                                value: intl.formatMessage(intlMessages.childPartialResponsibility)
                            }
                        ], horizontal: true, row: true, fullWidth: true, value: child.customerResponsibility, error: hasValidationError(isFieldTouched, child), helperText: hasValidationError(isFieldTouched, child) ? error : null, disabled: disabled, onChange: selectedValue => {
                            const newChildren = allChildren.map(c => {
                                if (c.ssn === child.ssn) {
                                    c.customerResponsibility = selectedValue;
                                }
                                return c;
                            });
                            dispatch({
                                type: "patch",
                                payload: {
                                    patches: [
                                        {
                                            target: calcRulesName,
                                            namespace: calcRulesNamespace,
                                            value: newChildren,
                                            overwrite: true,
                                            changeTrigger: `${calcRulesName}_manualSave`
                                        }
                                    ]
                                }
                            });
                        } }) })] }) }));
};
ChildListSection.displayName = "DployChildListSection";
export { ChildListSection };
//# sourceMappingURL=ChildListSection.js.map